import axios from "axios";
import imageCompression from "browser-image-compression";
import { FaRegFileLines } from "react-icons/fa6";
import { TbMailbox, TbRoute } from "react-icons/tb";
import { convertIsoToAmPm, formatDateToYYYYMMMDD } from "./date-helper";
import { prospect_possibility } from "./const-data";


export const mergeTwoArrayByKey = (arr1, arr2, key) => {
    // Merge Two Object Arrays by Key , Remove duplicate key of object
    // return Duplicate Key of object removed from second array 
    const set = new Set(arr1.map(obj => obj[key]));

    const merged = [
        ...arr1,
        ...arr2.filter(obj => !set.has(obj[key]))
    ];

    return merged;
}

export const compressImage = async (file, maxSizeMB = 1) => {
    const options = {
        maxSizeMB,  // Reduce to 
        useWebWorker: true,  // Faster compression
    };

    try {
        const compressedFile = await imageCompression(file, options);
        return compressedFile;

    } catch (error) {
        console.error("Compression Error:", error);
        return file;
    }
};

// Convert file to Base64
export const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};

export const networkSpeed = async () => {
    const startTime = performance.now();
    let duration = 0
    let speed = null
    let speedStatus = null

    try {
        const url = 'https://as2.ftcdn.net/v2/jpg/04/39/89/01/1000_F_439890152_sYbPxa1ANTSKcZuUsKzRAf9O7bJ1Tx5B.jpg?cb=' + Date.now();
        const response = await axios.get(url, { responseType: 'blob', });

        const endTime = performance.now();
        duration = endTime - startTime; // Time in milliseconds

        if (response.status === 200) {
            const responseSize = response.data.size; // Size in bytes
            const downloadTimeInSeconds = duration / 1000; // Convert ms to seconds

            // Calculate speed in kb/s (1 kb = 1024 bytes)
            speed = (responseSize / 1024) / downloadTimeInSeconds; // kb/s

            //internet speeds (in kb/s)
            if (speed < 128) {
                speedStatus = 'Slow'; // < 128 kb/s (~1 Mbps)
            } else if (speed < 1250) {
                speedStatus = 'Moderate'; // < 1250 kb/s (~10 Mbps)
            } else {
                speedStatus = 'Fast'; // >= 1250 kb/s (~10+ Mbps)
            }

            return { duration, speed, speedStatus };
        }
    } catch (error) {
        console.error('Error testing network speed:', error);
        return { duration, speed: null, speedStatus: 'Error' }; // Return fallback on error
    }
}

// Prospect router chart helper functions
const checkProspectCancellation = (data) => {
    const report = {
        isCancelled: false,
        stageIndex: 0,
        cancelledAt: null,
        cancelledBy: null,
        reason: null
    }

    const lastLead = data?.leads_list?.length > 0 ? data?.leads_list?.[data?.leads_list?.length - 1] : null
    if (lastLead?.lead_status === 5) {
        report.isCancelled = true;
        report.stageIndex = 1;
        report.cancelledAt = lastLead?.attended_at || lastLead?.date;
        report.cancelledBy = lastLead?.bucket_owner;
        report.reason = lastLead?.comment || 'No reason provided';

        return report;
    }

    const lastEnquiry = data?.enquiry_list?.length > 0 ? data?.enquiry_list?.[data?.enquiry_list?.length - 1] : null
    const lastWaitList = lastEnquiry?.wait_list_followup_list?.length > 0 ? lastEnquiry?.wait_list_followup_list?.[lastEnquiry?.wait_list_followup_list?.length - 1] : null
    if (lastEnquiry?.enquiry_status === 7) {
        report.isCancelled = true;
        report.stageIndex = 2;
        report.cancelledAt = lastWaitList?.attended_at || lastEnquiry?.area_visit_date || lastEnquiry?.followup_attended_at || lastEnquiry?.date;
        report.cancelledBy = lastEnquiry?.bucket_owner;
        report.reason = lastEnquiry?.comment || 'No reason provided';

        return report;
    }

    const lastInstallation = data?.installation_list?.length > 0 ? data?.installation_list?.[data?.installation_list?.length - 1] : null
    if (lastInstallation?.installation_status === 6) {
        report.isCancelled = true;
        report.stageIndex = 3;
        report.cancelledAt = lastInstallation?.followup_attended_at || lastInstallation?.date;
        report.cancelledBy = lastInstallation?.bucket_owner;
        report.reason = lastInstallation?.comment || 'No reason provided';

        return report;
    }

    return report;

}

const leadCardHelpers = (lead, index, lastLead, prospect_status, prospect_id) => {
    const cards = [];

    //1. proceed card
    index === 0 && cards.push({
        card_id: `lead_sec_${index + 1}_card_1`,
        card_title: `Proceed`,
        card_comment: `${lead?.action_by || lead?.bucket_owner} proceed the lead on ${new Date(lead?.date).toDateString()}.`,
        card_info: null,
        card_action: (prospect_status === 1 && [1, 2]?.includes(lead?.lead_status) && lastLead?._id === lead?._id) ? 1 : 0,  // 0 : closed , 1 : open
    });

    //2. followup 
    // 2A : convert to enquiry
    (lead?.lead_status === 3) && cards.push({
        card_id: `lead_sec_${index + 1}_card_2`,
        card_title: `Converted to Enquiry`,
        card_comment: `${new Date(lead?.attended_at).toDateString()} : ${lead?.action_by || lead?.bucket_owner} converted this lead to enquiry.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${lead?._id}/le_completed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2B : followup attend with move new date
    (lead?.attended_at && lead?.lead_status !== 3) && cards.push({
        card_id: `lead_sec_${index + 1}_card_2`,
        card_title: lead?.comment || `Follow-up completed`,
        card_comment: `${new Date(lead?.attended_at).toDateString()} : ${lead?.action_by || lead?.bucket_owner} handled this follow-up.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${lead?._id}/le_follow_up_closed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2C : followup not attend
    !lead?.attended_at && cards.push({
        card_id: `lead_sec_${index + 1}_card_2`,
        card_title: [4, 5]?.includes(lead?.lead_status) ? lead?.comment : `Follow-up on pending`,
        card_comment: `${new Date(lead?.date).toDateString()} : ${lead?.bucket_owner} handle the work.`,
        card_info: '',
        card_action: (prospect_status === 1 && lastLead?._id === lead?._id && ![4, 5]?.includes(lead?.lead_status)) ? 1 : 0,  // 0 : closed , 1 : open    
    })

    // if not complete the followup , then return
    if (!lead?.followup_attended_at || !lead?.scheduled_at_st) return cards;
    return cards
}

const enquiryCardHelpers = (enquiry, index, LastEnquiry, lastWaitList, prospect_status, prospect_id) => {
    const cards = [];

    //1. proceed card
    index === 0 && cards.push({
        card_id: `eq_sec_${index + 1}_card_1`,
        card_title: `Proceed`,
        card_comment: `${enquiry?.followup_by || enquiry?.bucket_owner} proceed the enquiry on ${new Date(enquiry?.date).toDateString()}.`,
        card_info: '',
        card_action: 0,  // 0 : closed , 1 : open
    });

    //2. followup 
    // 2A : followup to schedule
    (enquiry?.followup_attended_at && enquiry?.scheduled_at_st) && cards.push({
        card_id: `eq_sec_${index + 1}_card_2`,
        card_title: `Follow-up completed`,
        card_comment: `${new Date(enquiry?.followup_attended_at).toDateString()} : ${enquiry?.schedule_by || 'Not specified'} completed this follow-up and schedule a date.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_recall_closed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2B : followup attend with move new date
    (enquiry?.followup_attended_at && !enquiry?.scheduled_at_st) && cards.push({
        card_id: `eq_sec_${index + 1}_card_2`,
        card_title: enquiry?.comment || `Follow-up completed`,
        card_comment: `${new Date(enquiry?.followup_attended_at).toDateString()} : ${enquiry?.followup_by || enquiry?.bucket_owner} handled this follow-up.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_recall_closed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2C : followup not attend
    !enquiry?.followup_attended_at && cards.push({
        card_id: `eq_sec_${index + 1}_card_2`,
        card_title: [6, 7]?.includes(enquiry?.enquiry_status) ? enquiry?.comment : `Follow-up on pending`,
        card_comment: `${new Date(enquiry?.date).toDateString()} : ${enquiry?.bucket_owner} handle the work.`,
        card_info: '',
        card_action: (prospect_status === 2 && LastEnquiry?._id === enquiry?._id && ![6, 7]?.includes(enquiry?.enquiry_status)) ? 1 : 0,  // 0 : closed , 1 : open    
    })

    // if not complete the followup , then return
    if (!enquiry?.followup_attended_at || !enquiry?.scheduled_at_st) return cards;

    // 3. scheduled card
    (enquiry?.scheduled_at_st) && cards.push({
        card_id: `eq_sec_${index + 1}_card_3`,
        card_title: `Scheduled`,
        card_comment: `${enquiry?.schedule_by || 'Not specified'} scheduled the enquiry visit on ${new Date(enquiry?.scheduled_at_st).toDateString()} for ${enquiry?.area_visit_by || enquiry?.bucket_owner}.`,
        card_info: (prospect_status === 2 && LastEnquiry?._id === enquiry?._id && enquiry?.enquiry_status === 3) ? `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_on_scheduled` : null,
        card_action: (prospect_status === 2 && LastEnquiry?._id === enquiry?._id && enquiry?.enquiry_status === 3) ? 1 : 0,  // 0 : closed , 1 : open
    });

    // 4. enquiry cancel before visit
    (enquiry?.scheduled_at_st && [6, 7].includes(enquiry?.enquiry_status) && !enquiry?.area_visit_date) && cards.push({
        card_id: `eq_sec_${index + 1}_card_4`,
        card_title: enquiry?.comment,
        card_comment: `${enquiry?.bucket_owner} cancelled the enquiry before the visit on ${new Date(enquiry?.followup_attended_at).toDateString()}.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_cancelled`,
        card_action: 0,  // 0 : closed , 1 : open
    });

    // 5. area visit done
    (enquiry?.area_visit_date) && cards.push({
        card_id: `eq_sec_${index + 1}_card_5`,
        card_title: `Area Visit completed`,
        card_comment: `${new Date(enquiry?.area_visit_date).toDateString()} : ${enquiry?.area_visit_by || enquiry?.bucket_owner} completed the area visit.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_enquiry_visit`,
        card_action: 0,  // 0 : closed , 1 : open   
    });

    // 6. wait-list followup
    enquiry?.wait_list_followup_list?.length > 0 && enquiry?.wait_list_followup_list?.map((waitList, wIndex) => {
        // 6A. wait-list followup attended
        (waitList?.attended_at) && cards.push({
            card_id: `eq_sec_${index + 1}_card_6A.${wIndex}`,
            card_title: `Wait-list : Follow-up ${wIndex + 1} completed`,
            card_comment: `${new Date(waitList?.attended_at).toDateString()} : ${waitList?.attended_by || enquiry?.bucket_owner} completed this follow-up.`,
            card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_wait_list_closed?w_time=${waitList?.attended_at}`,
            card_action: 0,  // 0 : closed , 1 : open 
        })

        return null;
    });

    // 6B. last wait-list followup not attended
    (enquiry?.enquiry_status === 4) && cards.push({
        card_id: `eq_sec_${index + 1}_card_6B`,
        card_title: (enquiry?.enquiry_status === 5 ? enquiry?.comment : `Wait-list : Follow-up ${enquiry?.wait_list_followup_list?.length + 1} on pending`),
        card_comment: enquiry?.next_follow_up_date
            ? `${enquiry?.bucket_owner} assign the follow-up to ${new Date(enquiry?.next_follow_up_date).toDateString()}.`
            : `${enquiry?.bucket_owner} not assign a date to followup.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_on_wait_list`,
        card_action: (prospect_status === 2 && LastEnquiry?._id === enquiry?._id && enquiry?.enquiry_status === 4) ? 1 : 0,  // 0 : closed , 1 : open
    });

    // 7. cancelled after area visit
    ([6, 7]?.includes(enquiry?.enquiry_status) && enquiry?.area_visit_date) && cards.push({
        card_id: `eq_sec_${index + 1}_card_7`,
        card_title: enquiry?.comment,
        card_comment: `${enquiry?.bucket_owner} cancelled the enquiry after the area visit on ${new Date(lastWaitList?.attended_at).toDateString()}.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_cancelled`,
        card_action: 0,  // 0 : closed , 1 : open
    });

    // 8. convert to installation
    (enquiry?.enquiry_status === 5 || enquiry?.enquiry_status === 8) && cards.push({
        card_id: `eq_sec_${index + 1}_card_8`,
        card_title: `Pass to Installation`,
        card_comment: `${enquiry?.bucket_owner} pass the enquiry to installation on ${new Date(lastWaitList?.attended_at).toDateString()}.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${enquiry?._id}/eq_completed`,
        card_action: 0,  // 0 : closed , 1 : open
        card_buttons: [{
            button_className: 'info-fill',
            button_name: enquiry?.confirmed_enquiry_form,
            button_link: `/controller/prospect/e-form/${enquiry?.confirmed_enquiry_form}`
        }]
    })

    return cards
}

const installationCardHelpers = (installation, index, lastInstallation, prospect_status, prospect_id) => {
    const cards = [];

    //1. proceed card
    index === 0 && cards.push({
        card_id: `in_sec_${index + 1}_card_1`,
        card_title: `Proceed`,
        card_comment: `${installation?.followup_by || installation?.bucket_owner} proceed the installation on ${new Date(installation?.date).toDateString()}.`,
        card_info: '',
        card_action: (prospect_status === 3 && [1, 2]?.includes(installation?.installation_status) && lastInstallation?._id === installation?._id) ? 1 : 0,  // 0 : closed , 1 : open
    });

    //2. followup 
    // 2A : followup to schedule
    (installation?.followup_attended_at && installation?.scheduled_at_st) && cards.push({
        card_id: `in_sec_${index + 1}_card_2`,
        card_title: `Follow-up completed`,
        card_comment: `${new Date(installation?.followup_attended_at).toDateString()} : ${installation?.schedule_by || 'Not specified'} completed this follow-up and schedule a date.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_recall_closed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2B : followup attend with move new date
    (installation?.followup_attended_at && !installation?.scheduled_at_st) && cards.push({
        card_id: `in_sec_${index + 1}_card_2`,
        card_title: installation?.comment || `Follow-up completed`,
        card_comment: `${new Date(installation?.followup_attended_at).toDateString()} : ${installation?.followup_by || installation?.bucket_owner} handled this follow-up.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_recall_closed`,
        card_action: 0,  // 0 : closed , 1 : open
    });
    // 2C : followup not attend
    !installation?.followup_attended_at && cards.push({
        card_id: `in_sec_${index + 1}_card_2`,
        card_title: [5, 6]?.includes(installation?.installation_status) ? installation?.comment : `Follow-up on pending`,
        card_comment: `${new Date(installation?.date).toDateString()} : ${installation?.bucket_owner} handle the work.`,
        card_info: [5, 6]?.includes(installation?.installation_status) ? '' : `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_recall_pending`,
        card_action: (prospect_status === 3 && lastInstallation?._id === installation?._id && ![5, 6]?.includes(installation?.installation_status)) ? 1 : 0,  // 0 : closed , 1 : open    
    })

    // if not complete the followup , then return
    if (!installation?.followup_attended_at || !installation?.scheduled_at_st) return cards;

    // 3. scheduled card
    (installation?.scheduled_at_st) && cards.push({
        card_id: `in_sec_${index + 1}_card_3`,
        card_title: `Scheduled`,
        card_comment: `${installation?.schedule_by || 'Not specified'} scheduled the installation on ${new Date(installation?.scheduled_at_st).toDateString()} for ${installation?.area_visit_by || installation?.bucket_owner}.`,
        card_info: (prospect_status === 3 && lastInstallation?._id === installation?._id && installation?.installation_status === 3) ? `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_on_scheduled` : null,
        card_action: (prospect_status === 3 && lastInstallation?._id === installation?._id && installation?.installation_status === 3) ? 1 : 0,  // 0 : closed , 1 : open
    });

    // 4. installation cancel before visit
    (installation?.scheduled_at_st && [5, 6].includes(installation?.installation_status) && !installation?.area_visit_date) && cards.push({
        card_id: `in_sec_${index + 1}_card_4`,
        card_title: installation?.comment,
        card_comment: `${installation?.bucket_owner} cancelled the installation before the visit on ${new Date(installation?.followup_attended_at).toDateString()}.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_cancelled`,
        card_action: 0,  // 0 : closed , 1 : open
    });

    // 5. area visit done
    (installation?.area_visit_date) && cards.push({
        card_id: `in_sec_${index + 1}_card_5`,
        card_title: `Installation Completed`,
        card_comment: `${new Date(installation?.area_visit_date).toDateString()} : ${installation?.area_visit_by || installation?.bucket_owner} completed the installation.`,
        card_info: `/controller/prospect/v2/${prospect_id}/${installation?._id}/ins_completed`,
        card_action: 0,  // 0 : closed , 1 : open   
        card_buttons: [{
            button_className: 'secondary-fill',
            button_name: 'Installation Form',
            button_link: `/controller/prospect/i-form/${installation?.installation_srl_no}`
        }]
    });

    return cards;
}

// Prospect router chart handle function
export const convertToProspectRouterData = (data) => {

    const routerData = [];

    // prospect cancellation check
    const cancellation = checkProspectCancellation(data);

    // Prospect registered by
    const registrationData = {
        chapter_id: 'pr_rg',
        chapter_name: 'Registered',
        chapter_comment: `${data?.reg_by} registered the Prospect as ${data?.reg_type_text} on ${new Date(data?.reg_date).toDateString()}.`,
        chapter_status: 1,  // 1 : ok , 2 : cancelled, 3 : upcoming
        chapter_action: 0,  // 0 : closed , 1 : open
        chapter_type: 'card', // list , card
        card: {
            icon: <FaRegFileLines />,
            title: 'Prospect Registered',
            comment: `${data?.reg_by} registered the prospect as ${data?.reg_type_text} on ${new Date(data?.reg_date).toDateString()}`,
        },
        chapter_sections: []
    }
    routerData.push(registrationData);

    // BL Enquiry flow
    if (data?.bl_enquiry_list?.length > 0) {
        const LastEnquiry = data?.bl_enquiry_list?.length > 0 ? data?.bl_enquiry_list?.[data?.bl_enquiry_list?.length - 1] : null
        const lastWaitList = LastEnquiry?.wait_list_followup_list?.length > 0 ? LastEnquiry?.wait_list_followup_list?.[LastEnquiry?.wait_list_followup_list?.length - 1] : null
        const enquiryData = {
            chapter_id: 'bl_fl',
            chapter_name: 'Enquiry',
            chapter_comment: `The enquiry is ${LastEnquiry?.enquiry_status_text} under ${LastEnquiry?.bucket_owner} on ${new Date(lastWaitList?.attended_at || LastEnquiry?.attended_at || LastEnquiry?.date).toDateString()}.`,
            chapter_status: 1,  // 1 : ok, 2 : cancelled, 3 : upcoming
            chapter_action: (data?.prospect_status === 2 && !cancellation.isCancelled) ? 1 : 0,  // 0 : closed , 1 : open (if not cancelled)
            chapter_type: 'list', // list , card
            list: data?.bl_enquiry_list?.map((enquiry, index) => ({
                section_id: `bl_sec_${index + 1}`,
                cards_list: enquiryCardHelpers(enquiry, index, LastEnquiry, lastWaitList, data?.prospect_status, data?.prospect_id)
            })),
        }

        routerData.push(enquiryData);
    }

    // Lead flow
    if (data?.leads_list?.length > 0) {
        const LastLead = data?.leads_list?.length > 0 ? data?.leads_list?.[data?.leads_list?.length - 1] : null
        const leadData = {
            chapter_id: 'le_fl',
            chapter_name: 'Lead',
            chapter_comment: `The lead is ${LastLead?.lead_status_text} under ${LastLead?.bucket_owner} on ${new Date(LastLead?.attended_at || LastLead?.date).toDateString()}.`,
            chapter_status: 1,  // 1 : ok , 2 : cancelled, 3 : upcoming
            chapter_action: (data?.prospect_status === 1 && !cancellation.isCancelled) ? 1 : 0,  // 0 : closed , 1 : open (if not cancelled)
            chapter_type: 'list', // list , card
            list: data?.leads_list?.map((lead, index) => ({
                section_id: `lead_sec_${index + 1}`,
                cards_list: leadCardHelpers(lead, index, LastLead, data?.prospect_status, data?.prospect_id)
            })),

        }

        routerData.push(leadData);
    } else if (data?.reg_type_text === 'Lead' || data?.prospect_status === 1) {
        // if no lead but prospect registered as lead type , then show a card
        const leadData = {
            chapter_id: 'le_fl',
            chapter_name: 'Lead',
            chapter_comment: `No lead assigned to this prospect yet.`,
            chapter_status: 3,  // 1 : ok , 2 : cancelled, 3 : upcoming
        }

        routerData.push(leadData);
    }

    // Enquiry flow
    if (data?.enquiry_list?.length > 0) {
        const LastEnquiry = data?.enquiry_list?.length > 0 ? data?.enquiry_list?.[data?.enquiry_list?.length - 1] : null
        const lastWaitList = LastEnquiry?.wait_list_followup_list?.length > 0 ? LastEnquiry?.wait_list_followup_list?.[LastEnquiry?.wait_list_followup_list?.length - 1] : null
        const enquiryData = {
            chapter_id: 'eq_fl',
            chapter_name: 'Enquiry',
            chapter_comment: `The enquiry is ${LastEnquiry?.enquiry_status_text} under ${LastEnquiry?.bucket_owner} on ${new Date(lastWaitList?.attended_at || LastEnquiry?.attended_at || LastEnquiry?.date).toDateString()}.`,
            chapter_status: 1,  // 1 : ok, 2 : cancelled, 3 : upcoming
            chapter_action: (data?.prospect_status === 2 && !cancellation.isCancelled) ? 1 : 0,  // 0 : closed , 1 : open (if not cancelled)
            chapter_type: 'list', // list , card
            list: data?.enquiry_list?.map((enquiry, index) => ({
                section_id: `eq_sec_${index + 1}`,
                cards_list: enquiryCardHelpers(enquiry, index, LastEnquiry, lastWaitList, data?.prospect_status, data?.prospect_id)
            })),
        }

        routerData.push(enquiryData);
    } else if ((!data?.leads_list?.length && data?.reg_type_text === 'Enquiry') || cancellation.stageIndex !== 1) {
        // if no enquiry but prospect registered as enquiry type , then show a card
        // or if not cancelled in lead stage , then show a card
        const enquiryData = {
            chapter_id: 'eq_fl',
            chapter_name: 'Enquiry',
            chapter_comment: `No enquiry assigned to this prospect yet.`,
            chapter_status: 3,  // 1 : ok , 2 : cancelled, 3 : upcoming
        }

        routerData.push(enquiryData);
    }

    // Installation flow
    if (data?.prospect_status === 3) {
        const lastInstallation = data?.installation_list?.length > 0 ? data?.installation_list?.[data?.installation_list?.length - 1] : null
        const installationData = {
            chapter_id: 'ln_fl',
            chapter_name: 'Installation',
            chapter_comment: data?.installation_list?.length > 0
                ? `The installation is ${lastInstallation?.installation_status_text} under ${lastInstallation?.bucket_owner} on ${new Date(lastInstallation?.followup_attended_at || lastInstallation?.followup_attended_at || lastInstallation?.date).toDateString()}.`
                : "No installation assigned yet.",
            chapter_status: 1,  // 1 : ok, 2 : cancelled, 3 : upcoming
            chapter_action: (data?.prospect_status === 3 && ![4, 5, 6].includes(lastInstallation?.installation_status) && !cancellation.isCancelled) ? 1 : 0,  // 0 : closed , 1 : open (if not cancelled)
            chapter_type: !data?.installation_list?.length ? 'card' : 'list', // list , card
            list: data?.installation_list?.map((installation, index) => ({
                section_id: `eq_sec_${index + 1}`,
                cards_list: installationCardHelpers(installation, index, lastInstallation, data?.prospect_status, data?.prospect_id)
            })),
            card: !data?.installation_list?.length ? {
                icon: <TbMailbox />,
                title: "No Installation Assigned",
                comment: `No installation assigned to this prospect yet.`,
            } : undefined
        }

        routerData.push(installationData);
    } else if (![1, 2]?.includes(cancellation.stageIndex)) {
        // if not cancelled in enquiry stage , then show a card
        // or if not cancelled in lead stage , then show a card
        const installationData = {
            chapter_id: 'in_fl',
            chapter_name: 'Installation',
            chapter_comment: `No installation assigned to this prospect yet.`,
            chapter_status: 3,  // 1 : ok , 2 : cancelled, 3 : upcoming
        }

        routerData.push(installationData);
    }

    // Cancellation flow
    if (cancellation.isCancelled) {
        const cancellationData = {
            chapter_id: 'pc_fl',
            chapter_name: 'Cancelled',
            chapter_comment: `The prospect was cancelled by ${cancellation.cancelledBy} on ${new Date(cancellation.cancelledAt).toDateString()}.`,
            chapter_status: 2,  // 1 : ok , 2 : cancelled, 3 : upcoming
            chapter_action: 0,  // 0 : closed , 1 : open
            chapter_type: 'card', // list , card
            card: {
                icon: <TbRoute />,
                title: `Prospect Cancelled`,
                comment: `The prospect was cancelled by ${cancellation.cancelledBy} on ${new Date(cancellation.cancelledAt).toDateString()}.`,
            }
        }
        routerData.push(cancellationData);
    }

    return routerData;


}

// les table data setup
export const setupLesTableColumns = (lesType = null, selected = {}, worker = null, cities = []) => {
    const columns = [
        { header: 'Prospect ID', accessorKey: 'Prospect ID', enableHiding: false },
        { header: 'Full Name', accessorKey: 'Full Name' },
        { header: 'Place', accessorKey: 'Place' },
        { header: 'Post Office', accessorKey: 'Post Office' },
        { header: 'City', accessorKey: 'City' },
    ]
    const visibility = { Place: false, City: false, 'Reg Date': ['le_registration', 'le_out_of_bucket'].includes(selected?.status) }

    // City
    if (cities?.[0]) {
        visibility['City'] = true
    }

    // Non status
    if (selected?.type === 'nonStatus') {
        columns.push({ header: 'Current Status', accessorKey: 'Current Status' })
    }

    // Status
    switch (selected?.status) {
        // lead
        case 'le_registration':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Current Status', accessorKey: 'Current Status' })
            break;
        case 'le_out_of_bucket':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'le_follow_up_initial':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'le_follow_up_pending':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Assign Date', accessorKey: 'Assign Date' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'le_follow_up_closed':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Attended Date', accessorKey: 'Attended Date' })
            break;
        case 'le_completed':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;
        case 'le_cancelled':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;

        // Enquiry
        case 'eq_registration':
            columns.push({ header: 'Reg Date', accessorKey: 'Reg Date' })
            columns.push({ header: 'Current Status', accessorKey: 'Current Status' })
            break;
        case 'eq_out_of_bucket':
            columns.push({ header: 'R/C Date', accessorKey: 'R/C Date' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'eq_follow_up_initial':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'eq_recall_pending':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Assign Date', accessorKey: 'Assign Date' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'eq_recall_closed':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Attended Date', accessorKey: 'Attended Date' })
            break;
        case 'eq_on_scheduled':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Schedule Date', accessorKey: 'Schedule Date' })
            columns.push({ header: 'Schedule Time', accessorKey: 'Schedule Time' })
            break;
        case 'eq_enquiry_visit':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Visit Date', accessorKey: 'Visit Date' })
            break;
        case 'eq_on_wait_list':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Assign Date', accessorKey: 'Assign Date' })
            columns.push({ header: 'Priority', accessorKey: 'Priority' })
            break;
        case 'eq_wait_list_closed':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Attended Date', accessorKey: 'Attended Date' })
            break;
        case 'eq_completed':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;
        case 'eq_cancelled':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;

        // Installation
        case 'ins_out_of_bucket':
            break;
        case 'ins_follow_up_initial':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            break;
        case 'ins_recall_pending':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Followup Assign', accessorKey: 'Followup Assign' })
            break;
        case 'ins_recall_closed':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Attended Date', accessorKey: 'Attended Date' })
            break;
        case 'ins_on_scheduled':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Schedule Date', accessorKey: 'Schedule Date' })
            columns.push({ header: 'Schedule Time', accessorKey: 'Schedule Time' })
            break;
        case 'ins_completed':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;
        case 'ins_cancelled':
            columns.push({ header: 'Worker', accessorKey: 'Worker' })
            columns.push({ header: 'Closed At', accessorKey: 'Closed At' })
            break;
        default:
            break;
    }

    return [columns, visibility]
}

export const setupLesTableRows = (data = [], lesType = null, selected = {}, worker = null, cities = []) => {
    let filtered = selected?.type === 'nonStatus' ? data : data?.filter((i) => i?.work_data?.status_id === selected?.status)
    const newWindow = ['prospectWindow', 'width=1200,height=800,left=100,top=100']
    filtered = filtered?.map((work) => {
        const w_time = work?.work_data?.wait_list_attended_at
        const workObj = {
            "Prospect ID": work?.prospect_id,
            "Full Name": `${work?.first_name} ${work?.last_name}`,
            "Place": work?.address?.place || 'Nill',
            "Post Office": work?.address?.post || 'Nill',
            "City": work?.address?.city_name || 'Nill',
            city_id: work?.address?.city_id,
            _rowStyle: { cursor: 'Pointer' },
            _onClick: () => (work?.work_data?.status_id && !['le_out_of_bucket', 'eq_out_of_bucket', 'ins_out_of_bucket', 'le_registration', 'eq_registration',].includes(work?.work_data?.status_id))
                ? window.open(`/controller/prospect/v2/${work?.prospect_id}/${work?.work_id}/${work?.work_data?.status_id}?w_time=${w_time}`, ...newWindow)
                : window.open(`/controller/prospect/${work?.prospect_id}/view`, ...newWindow),
        }

        // Non status
        if (selected?.type === 'nonStatus') {
            const findText = lesType === 'lead' ? 'lead_status_text' : lesType === 'enquiry' ? 'enquiry_status_text' : lesType === 'installation' ? 'installation_status_text' : ''
            workObj['Current Status'] = work?.work_data?.[findText] || 'Status Close'
        }

        // Status
        switch (selected?.status) {
            // lead
            case 'le_registration':
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Current Status'] = work?.work_data?.lead_status_text || 'Status Close'
                break;
            case 'le_out_of_bucket':
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'le_follow_up_initial':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'le_follow_up_pending':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Assign Date'] = work?.work_data?.follow_up_assign_to ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_assign_to)) : 'Nill'
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'le_follow_up_closed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Attended Date'] = work?.work_data?.follow_up_attended_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_attended_at)) : 'Nill'
                break;
            case 'le_completed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Closed At'] = work?.work_data?.work_closed_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.work_closed_at)) : 'Nill'
                break;
            case 'le_cancelled':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Closed At'] = work?.work_data?.work_closed_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.work_closed_at)) : 'Nill'
                break;

            // Enquiry
            case 'eq_registration':
                workObj['Reg Date'] = work?.work_data?.reg_date ? formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date)) : 'Nill'
                workObj['Current Status'] = work?.work_data?.enquiry_status_text || 'Status Close'
                break;
            case 'eq_out_of_bucket':
                workObj['R/C Date'] = work?.work_data?.convert_date
                    ? `${formatDateToYYYYMMMDD(new Date(work?.work_data?.convert_date))} (C)` : `${formatDateToYYYYMMMDD(new Date(work?.work_data?.reg_date))} (R)`
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'eq_follow_up_initial':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'eq_recall_pending':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Assign Date'] = work?.work_data?.follow_up_assign_to ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_assign_to)) : 'Nill'
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'eq_recall_closed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Attended Date'] = work?.work_data?.follow_up_attended_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_attended_at)) : 'Nill'
                break;
            case 'eq_on_scheduled':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Schedule Date'] = work?.work_data?.scheduled_at_st ? formatDateToYYYYMMMDD(new Date(work?.work_data?.scheduled_at_st)) : 'Nill'
                workObj['Schedule Time'] = work?.work_data?.scheduled_at_st ? `${convertIsoToAmPm(new Date(work?.work_data?.scheduled_at_st))} to ${convertIsoToAmPm(new Date(work?.work_data?.scheduled_at_et))}` : 'Nill'
                break;
            case 'eq_enquiry_visit':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Visit Date'] = work?.work_data?.visit_in_time ? formatDateToYYYYMMMDD(new Date(work?.work_data?.visit_in_time)) : 'Nill'
                break;
            case 'eq_on_wait_list':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Assign Date'] = work?.work_data?.wait_list_assign_to ? formatDateToYYYYMMMDD(new Date(work?.work_data?.wait_list_assign_to)) : 'Nill'
                workObj['Priority'] = `${prospect_possibility[work?.work_data?.priority]}`
                break;
            case 'eq_wait_list_closed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Attended Date'] = work?.work_data?.wait_list_attended_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.wait_list_attended_at)) : 'Nill'
                break;
            case 'eq_completed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Closed At'] = work?.work_data?.work_closed_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.work_closed_at)) : 'Nill'
                break;
            case 'eq_cancelled':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Closed At'] = work?.work_data?.work_closed_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.work_closed_at)) : 'Nill'
                break;

            // Installation
            case 'ins_out_of_bucket':
                break;
            case 'ins_follow_up_initial':
                workObj['Worker'] = work?.work_data?.worker_name
                break;
            case 'ins_recall_pending':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Followup Assign'] = work?.work_data?.follow_up_assign_to ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_assign_to)) : 'Nill'
                break;
            case 'ins_recall_closed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Attended Date'] = work?.work_data?.follow_up_attended_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.follow_up_attended_at)) : 'Nill'
                break;
            case 'ins_on_scheduled':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Schedule Date'] = work?.work_data?.scheduled_at_st ? formatDateToYYYYMMMDD(new Date(work?.work_data?.scheduled_at_st)) : 'Nill'
                workObj['Schedule Time'] = work?.work_data?.scheduled_at_st ? `${convertIsoToAmPm(new Date(work?.work_data?.scheduled_at_st))} to ${convertIsoToAmPm(new Date(work?.work_data?.scheduled_at_et))}` : 'Nill'
                break;
            case 'ins_completed':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Closed At'] = work?.work_data?.visit_in_time ? formatDateToYYYYMMMDD(new Date(work?.work_data?.visit_in_time)) : 'Nill'
                break;
            case 'ins_cancelled':
                workObj['Worker'] = work?.work_data?.worker_name
                workObj['Closed At'] = work?.work_data?.work_closed_at ? formatDateToYYYYMMMDD(new Date(work?.work_data?.work_closed_at)) : 'Nill'
                break;
            default:
                break;
        }

        return workObj
    })

    return filtered
}

export const groupByDate = (array) => {
    if (!array?.length) return [];

    const grouped = array.reduce((acc, item) => {
        const date = new Date(item.scheduled_at_st).toISOString().split('T')[0];
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(item);
        return acc;
    }, {});

    // Convert to array of { date, items } and sort by date ascending
    return Object.entries(grouped)
        .map(([date, items]) => ({ date, items }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
}